import {Component, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges} from '@angular/core';
import { FormControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import config from 'devextreme/core/config';
import { custom } from 'devextreme/ui/dialog';
import { alert } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import repaintFloatingActionButton from 'devextreme/ui/speed_dial_action/repaint_floating_action_button';
import { Observable, of, Subject } from 'rxjs';
import {catchError, switchMap, take, takeUntil} from 'rxjs/operators';
import { Physician } from 'src/app/modules/shared/models/order-entry/physician';
import { ReportsService } from 'src/app/modules/shared/services/reports.service';
import { FusionAuthService } from '../../../../../shared/services/fusion-auth';
import {
    DateHelperService,
    DOMService,
    PWADetectorService,
    ScreenService,
    UrlProviderService,
} from '../../../../../shared/services/general';
import { LabSettingsService } from '../../../../../shared/services/ocl';
import { FileUtils } from '../../../../../shared/utils';
import {PatientSearchWithoutCollections} from '../../../../shared/models/order-entry/patient';
import { OrdersService } from '../../../../shared/services/orders.service';
import { Order, OrderStatus } from '../../models/order';
import {Document} from "../../../../shared/models/common/document";

@Component({
    selector: 'app-order-results',
    templateUrl: './order-results.component.html',
    styleUrls: ['./order-results.component.scss'],
})
export class OrderResultsComponent implements OnInit, OnDestroy {
    public isLoading = false;
    public docs: Document[] = [];
    public order = new Order();
    public abnormalLabel = '';
    public reportCommentFormControl = new FormControl();
    public clinicalResultsVisible = false;
    public toxicologyResultsVisible = false;
    public microbiologyResultsVisible = false;
    public pathologyResultsVisible = false;
    public selectedTab = -1;
    public orderId: number | null = -1;
    public isUploading = false;
    public emailFaxVisible = false;
    public type: string = '';
    public hasToxicologyUnexpectedPositive = false;
    public isPhoneOrTablet: boolean = false;
    public isPhone: boolean = false;
    private onDestroy$ = new Subject<void>();

    constructor(
        private domService: DOMService,
        public fusionAuthService: FusionAuthService,
        public ordersService: OrdersService,
        public labSettingsService: LabSettingsService,
        public router: Router,
        private pwaDetectorService: PWADetectorService,
        private urlProviderService: UrlProviderService,
        private sanitizer: DomSanitizer,
        public route: ActivatedRoute,
        private reportsService: ReportsService,
        public dateHelper: DateHelperService,
        private screen: ScreenService,
        private renderer: Renderer2,
    ) {
        this.onFileChanged = this.onFileChanged.bind(this);
    }

    public get Status() {
        let color;
        let status = OrderStatus[OrderStatus.None];
        switch (this.order.status) {
            case OrderStatus.Canceled:
                color = 'ocl-status-black';
                status = OrderStatus[OrderStatus.Canceled];
                break;
            case OrderStatus.Final:
                color = 'ocl-status-green';
                status = OrderStatus[OrderStatus.Final];
                break;
            case OrderStatus.Partial:
            case OrderStatus.PartiallyReleased:
            case OrderStatus.Preliminary:
                color = 'ocl-status-orange';
                status = OrderStatus[OrderStatus.Partial];
                break;
            case OrderStatus.Pending:
            case OrderStatus.Unreleased:
                color = 'ocl-status-yellow';
                status = OrderStatus[OrderStatus.Pending];
                break;
        }

        let alert = `<div fxLayout="column" fxLayoutAlign="center">
                        <div style="width: 100px;height:15px!important;line-height: .45 !important" class="${color}">
                        ${status.toUpperCase()}</div>`;

        if (this.order.hasAlert) {
            if (this.order.hasToxicologyUnexpectedPositive) {
                alert += `<div style="width: 100px;height:15px!important;line-height: .45 !important" class="ocl-status-critical">
                    ${this.abnormalLabel !== undefined && this.abnormalLabel !== null && this.abnormalLabel !== '' ?
                    this.abnormalLabel.toUpperCase() :
                    this.order.alert?.toUpperCase()}</div>`;
            }
            if (this.order.alert?.toUpperCase() === 'CRITICAL') {
                alert += `<div style="width: 100px;height:15px!important;line-height: .45 !important" class="ocl-status-critical">${this.order.alert}</div>`;
            }
        }
        alert += `</div>`;
        return this.sanitizer.bypassSecurityTrustHtml(alert);
    }

    public ngOnInit(): void {
        const request = this.route.paramMap.pipe(
            switchMap(params => {
                if (params.get('id')) {
                    this.isLoading = true;
                    //@ts-ignore
                    this.orderId = +params.get('id');

                    this.getResults();

                    return this.ordersService.getOder(this.orderId);
                } else {
                    return of(null);
                }
            }),
        );

        request
            .pipe(take(1))
            .subscribe((order: any) => {
                this.isLoading = false;
                this.order = order;

                if (order) {
                  this.ordersService.getOderDocuments(order.id).pipe(take(1)).subscribe(docs => this.docs = docs);
                  this.reportCommentFormControl.setValue(this.order.reportComment);
                }
            });

        this.labSettingsService.getToxicologySettings().pipe(take(1)).subscribe(s => {
            this.abnormalLabel = s.abnormalResult;
        });

        this.responsiveValues();
        this.screen.changed.pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                this.responsiveValues();
            });
    }

    private responsiveValues() {
        this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
        this.isPhone = this.screen.sizes['screen-x-small'];

        const shader = this.domService.getElementsByClassname('dx-overlay-shader').item(0) as HTMLElement;
        if (shader) {
            const button = shader.querySelector('.dx-fa-button-icon-close') as HTMLElement;
            if (button) {
                button.click();
            }
        }

        const offset = this.isPhone ? '-15 75' : '-25 75';
        config({
            floatingActionButtonConfig: {
                icon: 'rowfield',
                shading: true,
                direction: 'down',
                position: {
                    of: 'body',
                    my: 'right top',
                    at: 'right top',
                    offset,
                },
            },
        });
        repaintFloatingActionButton();
    }

    public get CancellationReasons() {
        if (!this.order) { return ''; }
        if (!this.order.cancellationReasons) { return ''; }
        return this.order ? this.order.cancellationReasons.map(c => c.name).join(', ') : '';
    }

    public get OrderPatient() {
      if (!this.order) { return new PatientSearchWithoutCollections(); }
      if (!this.order.patient) { return new PatientSearchWithoutCollections(); }
      return this.order.patient ? this.order.patient : new PatientSearchWithoutCollections();
    }

  public get OrderPhysician() {
    if (!this.order) {
      return new Physician();
    }
    if (!this.order.physician) {
      return new Physician();
    }
    return this.order.physician ? this.order.physician : new Physician();
  }

    public documentClick(name: string) {
        this.isLoading = true;
        if (this.orderId) {
          this.ordersService.downloadOrderDocuments(this.orderId, name)
            .pipe(
              take(1)
            )
            .subscribe((blob) => {
              if (!blob || blob?.size === 0) {
                notify('The document you are trying to open is not available now, please try again later.', 'warning');
                this.isLoading = false;
              } else {
                FileUtils.pdfRequestHandler(blob, this)
              }
            });
        }
    }

  public documentRemove(id: number) {
    const dialog = custom({
      title: 'Delete Document',
      messageHtml: 'Are you sure you want to remove the document from the order?',
      buttons: [{
        text: 'Yes',
        onClick: (e) => {
          return e.component.option('text');
        }
      }, {
        text: 'No',
        onClick: (e) => {
          return e.component.option('text');
        }
      }]});

    dialog.show().then((dialogResult: any) => {
      if (dialogResult === 'Yes') {
        this.isLoading = true;
        if (this.orderId) {
          this.ordersService.removeOrderDocument(this.orderId, id)
            .pipe(take(1))
            .subscribe(() => {
              this.isLoading = false;
              this.ordersService.getOderDocuments(this.orderId ? this.orderId : -1).pipe(take(1)).subscribe(docs => this.docs = docs);
              dialog.hide();
            });
        }
      } else {
        dialog.hide();
      }
    });

  }

    public printResults() {
        this.isLoading = true;
        if (this.orderId)
        this.reportsService.generateOrderResults([this.orderId])
            .pipe(take(1))
            .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this));
    }

    public onFileChanged(event: Event) {
        const response = FileUtils.validateFile(event as InputEvent, this);
        if (typeof response !== 'boolean') {
            response.then(fileString => {
                // @ts-ignore
              this.ordersService.uploadDocument(this.orderId, fileString, (event.target as HTMLInputElement).files[0].name)
                    .pipe(take(1))
                    .subscribe(res => {
                        this.isUploading = false;
                        this.isLoading = false;
                        if (res && res.id && this.orderId) {
                            notify('File uploaded successfully', 'success');
                            this.ordersService.getOderDocuments(this.orderId).pipe(take(1)).subscribe(docs => this.docs = docs);
                        }

                    }, () => {
                        notify('There was an error uploading your file', 'error');
                    });
            });
        }
    }

    public async openCumulativeResults() {
        if (this.pwaDetectorService.isPWA()) {
            await this.router.navigate(['/secured/results/cumulative', { id: this.orderId }]);
        } else {
            const url = this.router.serializeUrl(
                this.router.createUrlTree(['/secured/results/cumulative', { id: this.orderId }]),
            );
            window.open(url, '_blank');
        }
    }

    public openEmailFax(type: string) {
        this.type = type;
        this.emailFaxVisible = true;
    }

    public get Patient() {
      if (!this.order) return new PatientSearchWithoutCollections();
      if (!this.order.patient) return new PatientSearchWithoutCollections();
      return this.order.patient;
    }

    public viewerReady(element: any) {
        this.renderer.removeClass(element.element, 'dx-state-disabled');
    }

    public transformHtml(htmlTextWithStyle: string) {
        return this.sanitizer.bypassSecurityTrustHtml(htmlTextWithStyle);
    }

    public hasToxicologyUnexpectedPositiveResults(hasResults: boolean) {
        this.hasToxicologyUnexpectedPositive = hasResults;
    }

    public get IsCancelled() {
        return this.order ? this.order.status === OrderStatus.Canceled : false;
    }

    public onToolbarReady(e: any) {
        if (this.isPhone) {
            const items = e.element.querySelector('.dx-toolbar-before').children as HTMLCollection[];
            if (items[1]) {
                this.renderer.setStyle(items[1], 'vertical-align', 'baseline');
                this.renderer.setStyle(items[1], 'padding-top', '25px');
            }
        }
    }

    public get CollectionSites() {
        return this.order.collectionSites ? this.order.collectionSites.map(cs => cs.name).join(', ') : '';
    }

    public get Physician() {
      if (!this.order) {
        return '';
      }
      if (!this.order.physician) {
            return '';
        }
      return this.order.physician.lastName ? `${this.order.physician.lastName}, ${this.order.physician.firstName}` : '';
    }

    public get PatientPrimaryPhysician() {
      if (!this.order) { return ''; }
      if (!this.order.patient) {
            return '';
        }
      return this.order.patient.primaryPhysician ? `${this.order.patient.primaryPhysician.lastName},
            ${this.order.patient.primaryPhysician.firstName}` : '';
    }

    public get PatientAddress() {
      if (!this.order) {
        return '';
      }
      if (!this.order.patient) {
        return '';
      }
      if (!this.order.patient.zipCode) {
        return this.order.patient.address ? this.order.patient.address : '';
      }
      return `${this.order.patient.address}, ${this.order.patient.zipCode.city},
        ${this.order.patient.zipCode.state} ${this.order.patient.zipCode ? this.order.patient.zipCode.code : ''}`;
    }

    public get PatientCode() {
      if (!this.order) {
        return '';
      }
      return this.order.patient ? this.order.patient.code : '';
    }

    public get Room() {
      if (!this.order) {
        return '';
      }
      if (!this.order.patient) { return ''; }
      return this.order.patient.room ? this.order.patient.room.name : '';
    }

    public get Client() {
      if (!this.order) {
        return '';
      }
      if (!this.order.patient) { return ''; }
      return this.order.patient.client ? this.order.patient.client.name : '';
    }

    public get PatientFullName() {
      if (!this.order) {
        return '';
      }
      if (!this.order.patient) { return ''; }
      return `${this.order.patient.firstName} ${this.order.patient.middleName ? this.order.patient.middleName : ''}
        ${this.order.patient.lastName}`;
    }

    public get DOB() {
      if (!this.order) {
        return '';
      }
      if (!this.order.patient) { return ''; }
      return this.order.patient.birthDate ?
            `(${this.dateHelper.dateWithFormatWithoutUtc(this.order.patient.birthDate as Date, 'LL')})` : '';
    }

    public get Gender() {
      if (!this.order) {
        return '';
      }
      if (!this.order.patient) { return ''; }
      return this.order.patient.gender;
    }

    public get Unit() {
      if (!this.order) {
        return '';
      }
      if (!this.order.patient) { return ''; }
      if (!this.order.patient.unit) { return ''; }
      return this.order.patient.unit.name;
    }

    public get CollectionDate() {
      if (!this.order.collectionDate) return '';
        return this.dateHelper.dateWithFormatWithoutUtc(this.order.collectionDate, 'MM/DD/YYYY hh:mm a');
    }

    public get ReceivedDate() {
      if (!this.order.receivedDate) return '';
        return this.dateHelper.dateWithFormatWithoutUtc(this.order.receivedDate, 'MM/DD/YYYY hh:mm a');
    }

    public get ReportedDate() {
      if (!this.order.reported) return '';
        return this.dateHelper.dateWithFormat(this.order.reported, 'MM/DD/YYYY hh:mm a');
    }

    public get IsLoading() {
        return this.isLoading;
    }

    public set IsLoading(loading) {
        this.isLoading = loading;
    }

    public onButtonReady(e: any) {
        if (e.actionElement && e.actionElement.length > 0) {
            const parent = e.actionElement[0];
            const labelButtons = parent.querySelector('.dx-overlay-content');
            if (labelButtons !== undefined) {
                this.renderer.setStyle(labelButtons, 'overflow', 'visible');
            }
        }
    }

    private getResults() {
      if(this.orderId)
        this.ordersService
            .getOrderTestsTypes(this.orderId)
            .pipe(take(1))
            .subscribe(response => {
                this.clinicalResultsVisible = response.hasClinicalResult;
                this.microbiologyResultsVisible = response.hasMicrobiologyResult;
                this.toxicologyResultsVisible = response.hasToxicologyResult;
                this.pathologyResultsVisible = response.hasPathologyResult;
                if (!this.clinicalResultsVisible && !this.microbiologyResultsVisible && !this.toxicologyResultsVisible && !this.pathologyResultsVisible) {
                    alert('There is no result to display.', 'No results').then();
                } else {
                  this.selectedTab = this.clinicalResultsVisible
                    ? 0
                    : this.toxicologyResultsVisible
                      ? 2
                      : this.microbiologyResultsVisible
                        ? 1
                        : this.pathologyResultsVisible
                          ? 3
                          : -1;
                }
            });
    }

  public get ClientAddress() {
    if (!this.order.patientClient) {
      return '';
    }
    if (!this.order.patientClient.zipCode) {
      return this.order.patientClient.address ? this.order.patientClient.address : '';
    }
    return `${this.order.patientClient.address}, ${this.order.patientClient.zipCode.city},
        ${this.order.patientClient.zipCode.state} ${this.order.patientClient.zipCode ? this.order.patientClient.zipCode.code : ''}`;
  }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}
